import { Table } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import CopyOutlined from '@ant-design/icons/lib/icons/CopyOutlined';
import { ColumnsType, TablePaginationConfig, TableRowSelection } from 'antd/lib/table/interface';
import { CommandButton } from 'components/ui/composed/dashboard/DashboardTable';
import ListTags from 'components/ui/composed/tag/ListTags';
import { ListPayload } from 'core/http/response';
import { FC, useCallback, useMemo, Dispatch, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProductTemplate } from 'store/products-templates/products';
import { FlexDiv, SpanEllipsis } from '../../custom-declaration/declaration-table/components/DeclarationsTable.styled';
import { TemplateResponse } from 'store/template/template';
import { capitalize, isEmpty } from 'lodash';
import { AddProductTemplatesToDeclaration } from '../../declarations/common/DeclarationsProductsTemplates';
import useProductsTemplates from 'hooks/useProductsTemplates';
import { ListProductTemplateParams } from 'store/products-templates/client';
import { DeleteProductTemplates } from '../../custom-declaration/products/ProductTemplatesDashboard';
import { getTableChangeParams } from 'utils/tableHelpers';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { DeclarationCountry } from 'store/declarations/enums/common/declaration-country';
import { colors } from 'theme';
import { SBox44Button } from 'views/declarations/common/box44/components/styles';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';

export type Product<TExtends = unknown> = ProductTemplate<TExtends>;

export const getDescriptionOfGoods = (product: Product<any>) => {
    const irelandH1DescriptionOfGoods = product?.goodsInformation?.goodsDescription;
    const irelandH7DescriptionOfGoods = product?.descriptionOfGoods;
    const ukH1DescriptionOfGoods = product?.goodsDescription;
    const ukB1EnsDescriptionOfGoods = product?.commodity?.description;
    const aesDescriptionOfGoods = product?.commodity?.descriptionOfGoods;

    const descriptionOfGoods =
        irelandH1DescriptionOfGoods ??
        irelandH7DescriptionOfGoods ??
        ukH1DescriptionOfGoods ??
        ukB1EnsDescriptionOfGoods ??
        aesDescriptionOfGoods ??
        '-';

    return descriptionOfGoods;
};

interface Props {
    source: ListPayload<Product>;
    showCommands?: boolean;
    deleteProductTemplatesIds?: string[];
    setDeleteProductTemplates?: Dispatch<Partial<DeleteProductTemplates>>;
    declarationTemplatesList?: TemplateResponse[] | null;
    onAddProductTemplatesToDeclaration?: Dispatch<Partial<AddProductTemplatesToDeclaration>>;
    country: DeclarationCountry;
    messageType: MessageTypes;
    internalType: DeclarationInternalType;
    newUi?: boolean;
    search: string;
    loading?: boolean;
    onRequestList: (params?: Partial<ListProductTemplateParams>) => void;
}

const ProductTemplatesTable: FC<Props> = ({
    source,
    showCommands,
    deleteProductTemplatesIds,
    setDeleteProductTemplates,
    declarationTemplatesList,
    onAddProductTemplatesToDeclaration,
    country,
    messageType,
    internalType,
    search,
    loading,
    onRequestList,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { saveProductTemplate } = useProductsTemplates({ country, internalType, messageType });

    const rowSelection: TableRowSelection<any> = {
        type: 'checkbox',
        onChange: (_selectedRowKeys: any, selectedRows: Product[]) => {
            const selectedProductTemplateIds = selectedRows.reduce((acc: string[], row) => {
                if (typeof row?.id === 'string') acc.push(row.id);
                return acc;
            }, []);

            onAddProductTemplatesToDeclaration?.({ productTemplatesIds: selectedProductTemplateIds });
            setDeleteProductTemplates?.({ productTemplateIds: selectedProductTemplateIds });
        },
    };

    const handleDuplicate = useCallback(
        async ({ id, ...rest }: Product) => saveProductTemplate(rest)?.then(() => onRequestList()),
        [saveProductTemplate, onRequestList]
    );

    const handleTags = (tags: string[]) => {
        if (tags?.length) {
            return <ListTags tags={tags} vertical />;
        } else {
            return <span>-</span>;
        }
    };

    const getProductType = () => {
        return (
            <FlexDiv>
                <SpanEllipsis>{capitalize(country.toLowerCase())}</SpanEllipsis>
                <SpanEllipsis>{`${capitalize(internalType)} (${messageType ?? '-'})`}</SpanEllipsis>
            </FlexDiv>
        );
    };

    const getCommodityCode = (product: Product<any>) => {
        const irelandH1CommodityCode = product?.goodsInformation?.combinedNomenclatureCode;
        const irelandH7CommodityCode = product?.commodityCodeHarmonizedSystemSubHeadingCode;
        const ukH1CommodityCode = product?.commodityCombinedNomenclatureTypeCode;
        const ukB1CommodityCode = product?.commodity?.classification?.[0]?.id;
        const ensCommodityCode = product?.commodity?.combinedNomenclature;
        const aesCommodityCode = product?.commodity?.commodityCode?.harmonizedSystemSubHeadingCode;

        const commodityCode =
            irelandH1CommodityCode ??
            irelandH7CommodityCode ??
            ukH1CommodityCode ??
            ukB1CommodityCode ??
            ensCommodityCode ??
            aesCommodityCode ??
            '-';

        return <SpanEllipsis>{commodityCode}</SpanEllipsis>;
    };

    const getItemPrice = (product: Product<any>) => {
        const irelandH1ItemPrice = product?.itemAmount;
        const irelandH7ItemPrice = product?.itemAmountInvoicedIntrinsicValue?.valueAmount;
        const ukH1ItemPrice = product?.itemPriceAmount;
        const ukB1ItemPrice = product?.statisticalValueAmount;
        const aesItemPrice = product?.statisticalValue;

        const itemPrice =
            irelandH1ItemPrice ?? irelandH7ItemPrice ?? ukH1ItemPrice ?? ukB1ItemPrice ?? aesItemPrice ?? '-';

        return <SpanEllipsis>{itemPrice}</SpanEllipsis>;
    };

    const declarationTemplateNames = useMemo(
        () =>
            declarationTemplatesList?.reduce((acc, template) => {
                acc.set(template.id, template.templateName);
                return acc;
            }, new Map()),
        [declarationTemplatesList]
    );

    const getDeclarationTemplate = (product: Product) => {
        const declarationTemplateName = declarationTemplateNames?.get(product?.declarationTemplateId);
        return <SpanEllipsis>{declarationTemplateName ?? 'Default'}</SpanEllipsis>;
    };

    const [tablePagination, setTablePagination] = useState<TablePaginationConfig>({
        current: 1,
        total: source?.total,
        showSizeChanger: true,
        hideOnSinglePage: true,
        pageSize: source?.pageSize,
        position: ['bottomCenter'],
    });

    useEffect(() => {
        if (!source) return;
        const { pageSize, total } = source;
        setTablePagination((prev) => ({
            ...prev,
            pageSize,
            total,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source.pageSize, source.total]);

    useEffect(() => {
        setTablePagination((prev) => ({ ...prev, current: 1 }));
    }, [search, country, messageType, internalType]);

    const [tableSorter, setTableSorter] = useState<any>(undefined);

    useEffect(() => {
        const query = search || undefined;
        onRequestList({
            messageType,
            query,
            ...getTableChangeParams({ pagination: tablePagination, sorter: tableSorter }),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, country, messageType, internalType, tablePagination, tableSorter]);

    const columns: ColumnsType<Product> = [
        {
            title: 'Form',
            dataIndex: 'formType',
            render: (_text: string, _record: Product) => getProductType(),
            sorter: true,
        },
        {
            title: 'Template Name',
            dataIndex: 'declaration_template',
            key: 'declaration_template',
            render: (_text: string, record: Product) => record && getDeclarationTemplate(record),
            filterMode: 'tree',
            sorter: true,
        },
        {
            title: 'Commodity Code',
            dataIndex: 'commodity_code',
            key: 'commodity_code',
            width: 197,
            render: (_text: string, record: Product) => record && getCommodityCode(record),
            sorter: true,
        },
        {
            title: 'Description of Goods',
            dataIndex: 'description_of_goods',
            key: 'description_of_goods',
            render: (_text: string, record: Product) =>
                record && <SpanEllipsis>{getDescriptionOfGoods(record)}</SpanEllipsis>,
            sorter: true,
        },
        {
            title: 'Item Price',
            dataIndex: 'item_price',
            key: 'item_price',
            render: (_text: string, record: Product) => record && getItemPrice(record),
            sorter: true,
        },
        {
            title: 'Tag',
            dataIndex: 'tag',
            key: 'tag',
            render: (_text: string, record: Product) => handleTags(record.tags ?? []),
            sorter: true,
        },
        {
            title: 'Actions',
            dataIndex: 'commands',
            key: 'commands',
            align: 'center',
            render: (_text: string, record: Product) => (
                <>
                    <CommandButton
                        style={{ marginRight: '1.6rem' }}
                        tooltip="Duplicate"
                        icon={
                            <CopyOutlined
                                style={{
                                    color: colors.brand6,
                                }}
                            />
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicate?.(record);
                        }}
                    />
                    <CommandButton
                        tooltip="Delete"
                        icon={
                            <DeleteOutlined
                                style={{
                                    color: colors.brand6,
                                }}
                            />
                        }
                        onClick={(e) => {
                            e.stopPropagation();
                            console.log(record);
                            if (!record?.id) return;
                            setDeleteProductTemplates?.({
                                modalVisible: true,
                                productTemplateIds: [record.id],
                            });
                        }}
                    />
                </>
            ),
        },
    ];

    const getColumns = () => (showCommands ? columns : columns.filter((c) => c.title !== 'Commands'));

    return (
        <>
            <StyledTableWrapper>
                <Table
                    rowKey={(record: Product) => source?.list.findIndex((r) => record === r)}
                    columns={getColumns()}
                    dataSource={source.list}
                    rowSelection={rowSelection}
                    loading={loading}
                    pagination={tablePagination}
                    onRow={(record: any) => {
                        return {
                            onClick: () => {
                                if (location.pathname.includes(`/declarations`)) {
                                    onAddProductTemplatesToDeclaration?.({
                                        productTemplatesIds: [record.id],
                                        modalVisible: true,
                                    });
                                } else {
                                    navigate(
                                        `/customs-declarations/${country}/${internalType}/products/${
                                            messageType || 'ENS'
                                        }/${record.id}`
                                    );
                                }
                            },
                        };
                    }}
                    components={{
                        header: {
                            cell: (props: any) => <StyledTableHeaderCell {...props} />,
                        },
                    }}
                    onChange={(pagination, _, sorter) => {
                        setTablePagination(pagination);
                        setTableSorter(sorter);
                    }}
                />
            </StyledTableWrapper>

            {!isEmpty(deleteProductTemplatesIds) && (
                <SBox44Button
                    icon={<DeleteOutlined />}
                    onClick={() => setDeleteProductTemplates?.({ modalVisible: true })}
                >
                    Delete
                </SBox44Button>
            )}
        </>
    );
};
export default ProductTemplatesTable;
